import type { DbClient } from "@tanstack/react-db";
import { isCancelledError, type QueryClient } from "@tanstack/react-query";
import type {
    FindFeedInput,
    SearchTerminalContext,
} from "@wags/discovery/index.ts";
import { DEFAULT_MAX_DISTANCE_MILES } from "@wags/discovery/pure.ts";
import type { SearchParams } from "@/features/discovery";
import { getSavedListingIdsCollection } from "@/features/saved-listings";
import type {
    InitialGeoContext,
    InitialGeoLocation,
} from "@/features/viewer-location";
import {
    readSelectedLocation,
    resolveRouteApproximateLocation,
} from "@/features/viewer-location";
import { TimeoutError, withTimeout } from "@/lib/core/with-timeout";
import type { FindSearch } from "./find-route.contract.ts";
import {
    findFeedQueryInput,
    getFindFeedQueryOptions,
} from "../feed/find-feed-query";
import { FIND_FEED_RETRY_OPTIONS } from "../find/find-load-failure";
import { prefetchInfiniteSearch } from "../search";
import { hasActiveFindFilters } from "./browse-route-helpers";
import {
    createObservedSearchContext,
    resolveSearchNavigationContext,
} from "@/features/discovery";

export type FindRoutePath = "/" | "/find";
export type PendingFindFilters = Partial<Omit<SearchParams, "page" | "limit">>;

export function bindFindLocation(
    search: FindSearch,
    estimatedLocation: InitialGeoLocation | null | undefined,
    selectedLocation = readSelectedLocation(),
): FindSearch {
    if (search.lat != null && search.lng != null) return search;
    const location = selectedLocation ?? estimatedLocation;
    if (!location) return search;
    const shouldSetDefaultDistance =
        search.maxDistanceMiles === undefined && !hasActiveFindFilters(search);
    return {
        ...search,
        lat: location.lat,
        lng: location.lng,
        locationSource: selectedLocation ? undefined : "estimated",
        maxDistanceMiles: shouldSetDefaultDistance
            ? DEFAULT_MAX_DISTANCE_MILES
            : search.maxDistanceMiles,
    };
}

export async function findBeforeLoad({
    context,
    location,
    search,
}: {
    context: InitialGeoContext;
    location: { href: string; state: object | null };
    search: FindSearch;
}) {
    const hasLocation = search.lat != null && search.lng != null;
    const estimatedLocation = hasLocation
        ? null
        : await resolveRouteApproximateLocation(context);

    const navigationContext =
        typeof location.state === "object" && location.state !== null
            ? resolveSearchNavigationContext(location.state)
            : null;
    if (navigationContext) {
        return { searchCorrelation: navigationContext, estimatedLocation };
    }

    const locationState = location.state;
    const locationKey =
        locationState !== null &&
        "__TSR_key" in locationState &&
        typeof locationState.__TSR_key === "string"
            ? locationState.__TSR_key
            : location.href;
    return {
        estimatedLocation,
        searchCorrelation:
            await context.queryClient.ensureQueryData<SearchTerminalContext>({
                queryKey: ["observed-search-context", locationKey],
                queryFn: () =>
                    createObservedSearchContext({
                        query: search.q ?? search.dogName ?? "",
                    }),
                staleTime: Number.POSITIVE_INFINITY,
            }),
    };
}

export const findLoaderDeps = ({ search }: { search: FindSearch }) => search;

export type FindSearchPromise = Promise<Awaited<
    ReturnType<typeof prefetchInfiniteSearch>
> | null>;

export type FindLoaderData = {
    searchPromise?: FindSearchPromise;
    feedReadyPromise?: Promise<void>;
};

export const FIND_FEED_STREAM_TIMEOUT_MS = 12_000;

const coarseCoordinate = (value: number) => Math.round(value * 10) / 10;

async function waitForServerFeed(
    queryClient: QueryClient,
    feedQuery: ReturnType<typeof getFindFeedQueryOptions>,
    feedInput: FindFeedInput,
) {
    const logContext = {
        coarseLat: coarseCoordinate(feedInput.lat),
        coarseLng: coarseCoordinate(feedInput.lng),
    };
    try {
        return await withTimeout(
            queryClient.ensureQueryData(feedQuery),
            FIND_FEED_STREAM_TIMEOUT_MS,
        );
    } catch (error: unknown) {
        if (error instanceof TimeoutError) {
            console.warn("[find] feed stream wait timed out", {
                ...logContext,
                timeoutMs: FIND_FEED_STREAM_TIMEOUT_MS,
            });
            void queryClient.cancelQueries({
                queryKey: feedQuery.queryKey,
                exact: true,
            });
        } else if (!isCancelledError(error)) {
            console.error("[find] feed load failed", {
                ...logContext,
                message: error instanceof Error ? error.message : String(error),
            });
        }
        return null;
    }
}

export const findLoader = ({
    context,
    deps,
}: {
    context: {
        dbClient: DbClient;
        queryClient: Parameters<typeof prefetchInfiniteSearch>[0];
        searchCorrelation: SearchTerminalContext;
        estimatedLocation?: InitialGeoLocation | null;
    };
    deps: FindSearch;
}): FindLoaderData => {
    if (typeof window !== "undefined") {
        void getSavedListingIdsCollection(context.dbClient)
            .preload()
            .catch(() => {});
    }

    const search = bindFindLocation(deps, context.estimatedLocation);
    if (search.lat == null || search.lng == null) {
        return {};
    }

    if (hasActiveFindFilters(search)) {
        const searchPromise: FindSearchPromise = prefetchInfiniteSearch(
            context.queryClient,
            context.dbClient,
            {
                ...search,
                lat: search.lat,
                lng: search.lng,
                page: search.page ?? 0,
            },
            context.searchCorrelation,
            undefined,
            FIND_FEED_RETRY_OPTIONS,
        ).catch(() => null);
        return {
            searchPromise,
        };
    }

    const feedInput = findFeedQueryInput({ lat: search.lat, lng: search.lng });
    const feedQuery = getFindFeedQueryOptions(feedInput);
    const feedPromise =
        typeof window === "undefined"
            ? waitForServerFeed(context.queryClient, feedQuery, feedInput)
            : context.queryClient.ensureQueryData(feedQuery).catch(() => null);
    return {
        feedReadyPromise: feedPromise.then(() => undefined),
    };
};
